/**
 * The two-mode LinkedIn integration, ported — `m05b-linkedin-api.js` §A/§F and `m02-mockdb.js:16`.
 *
 * The reference keeps this state in a module-level singleton rather than in the demo database, and
 * so does this port: it is a prototype scenario switch, not a record. A settings screen that put it
 * in the seed would make «simülasyon açık» survive a reset, which the reference's own Kaydet does
 * not do.
 *
 * Nothing here performs OAuth, calls an API or writes a profile. `apiAccessStatus` stays
 * `not_applied` until a real LinkedIn approval says otherwise (binding rule 15), and the simulation
 * switch only opens the prototype's screens.
 */

export const LINKEDIN_ADD_TO_PROFILE_URL =
  'https://www.linkedin.com/profile/add?startTask=CERTIFICATION_NAME';

/** `LINKEDIN.isLinkedInHost` — https only, and the host must actually be linkedin.com. */
export function isLinkedInHost(url: string): boolean {
  try {
    const u = new URL(url);
    return u.protocol === 'https:' && /(^|\.)linkedin\.com$/.test(u.hostname);
  } catch {
    return false;
  }
}

export const LINKEDIN_STATUS_TR: Record<string, string> = {
  not_applied: 'Başvuru yapılmadı',
  application_preparing: 'Başvuru hazırlanıyor',
  submitted: 'Başvuru gönderildi',
  pending_linkedin_approval: 'LinkedIn onayı bekleniyor',
  approved: 'Onaylandı',
  rejected: 'Reddedildi',
  suspended: 'Askıya alındı',
  unavailable: 'Kullanılamıyor',
  not_connected: 'Bağlı değil',
  authorization_required: 'Yetkilendirme gerekli',
  connected: 'Bağlı',
  token_expired: 'Token süresi doldu',
  permission_revoked: 'İzin geri alındı',
};

export const LINKEDIN_REFERENCES: { label: string; url: string }[] = [
  {
    label: 'LinkedIn Add to Profile FAQ',
    url: 'https://www.linkedin.com/help/linkedin/answer/a528030',
  },
  {
    label: 'Profile Edit API — Certifications',
    url: 'https://learn.microsoft.com/en-us/linkedin/shared/integrations/people/profile-edit-api/certifications',
  },
  {
    label: 'LinkedIn API access and permissions',
    url: 'https://learn.microsoft.com/en-us/linkedin/shared/authentication/getting-access',
  },
];

/**
 * The verified organization reference. `source` is `unverified_placeholder` and the settings screen
 * says so out loud: this is not a free-text field an administrator may type an URN into, it is
 * established and audited during integration setup.
 */
export const LINKEDIN_ORGANIZATION_REFERENCE = {
  urn: 'urn:li:organization:HPW-PROTOTYPE-PLACEHOLDER',
  verifiedAt: null as string | null,
  verifiedBy: null as string | null,
  source: 'unverified_placeholder',
  pageUrl: 'https://www.linkedin.com/company/happyplacetowork/',
  displayName: 'Happy Place to Work Türkiye',
};

const LINKEDIN_INTEGRATION_MODES = ['static_add_to_profile', 'profile_edit_api'];
const LINKEDIN_MEMBER_CONNECTION_STATUSES = [
  'not_connected',
  'authorization_required',
  'connected',
  'token_expired',
  'permission_revoked',
];
const LINKEDIN_SYNC_STATUSES = [
  'not_started',
  'api_access_unavailable',
  'member_authorization_required',
  'ready',
  'queued',
  'syncing',
  'synced',
  'failed',
  'token_expired',
  'permission_revoked',
  'remote_record_deleted',
];

/**
 * The two backend entities, as SHAPE only. The settings card prints these with
 * `JSON.stringify(…, null, 2)`, so the key order below is the order the reader sees.
 */
export const CREDENTIAL_LINKEDIN_SYNC_SHAPE: Record<string, unknown> = {
  id: 'string',
  credentialId: 'string',
  linkedinMemberConnectionId: 'string|null',
  integrationMode: LINKEDIN_INTEGRATION_MODES,
  syncStatus: LINKEDIN_SYNC_STATUSES,
  linkedinCertificationId: 'string|null',
  requestFingerprint: 'sha256',
  idempotencyKey: 'string',
  requestedAt: 'rfc3339|null',
  authorizedAt: 'rfc3339|null',
  queuedAt: 'rfc3339|null',
  syncingAt: 'rfc3339|null',
  syncedAt: 'rfc3339|null',
  failedAt: 'rfc3339|null',
  lastErrorCode: 'string|null',
  lastErrorMessage: 'string|null',
  retryCount: 'integer',
  permissionRevokedAt: 'rfc3339|null',
  createdAt: 'rfc3339',
  updatedAt: 'rfc3339',
};

export const LINKEDIN_MEMBER_CONNECTION_SHAPE: Record<string, unknown> = {
  id: 'string',
  subjectId: 'string',
  providerMemberId: 'string',
  status: LINKEDIN_MEMBER_CONNECTION_STATUSES,
  scopesSnapshot: 'string[]',
  authorizedAt: 'rfc3339|null',
  expiresAt: 'rfc3339|null',
  revokedAt: 'rfc3339|null',
  /* Never a token value: the backend stores a vault reference and the prototype says which. */
  tokenReference: 'vault-reference (asla token değeri)',
  createdAt: 'rfc3339',
  updatedAt: 'rfc3339',
};

export const LINKEDIN_CONTRACT_RULES: string[] = [
  'Access token, refresh token veya client secret Credential içinde TUTULMAZ.',
  'tokenReference yalnız backend secret/token vault referansıdır; token değeri değildir.',
  'OAuth authorization code akışı server-side tamamlanır; client secret yalnız server-side tutulur.',
  'API write idempotent komut olarak işlenir (idempotencyKey + requestFingerprint).',
  "LinkedIn'in döndürdüğü certification ID saklanır (linkedinCertificationId).",
  'Her sync işlemi actor + reason + correlationId ile audit edilir.',
  'Token expiry ve permission revocation ayrı durumlar olarak yönetilir.',
  'Kullanıcı LinkedIn bağlantısını istediği anda kaldırabilir.',
  'Credential revoke edildiğinde uzak LinkedIn kaydının otomatik silineceği VARSAYILMAZ; ayrı ve izinli reconciliation akışı gerekir.',
  'Üye izni olmadan profile write YAPILMAZ.',
];

export interface LinkedInIntegrationState {
  integrationMode: string;
  apiAccessStatus: string;
  memberConnectionStatus: string;
  simulated: boolean;
  organizationReferenceVerified: boolean;
}

/** The real default. While `simulated` is false no API path is open anywhere in the product. */
const REAL_STATE: LinkedInIntegrationState = {
  integrationMode: 'static_add_to_profile',
  apiAccessStatus: 'not_applied',
  memberConnectionStatus: 'not_connected',
  simulated: false,
  organizationReferenceVerified: LINKEDIN_ORGANIZATION_REFERENCE.source === 'verified_configuration',
};

let current: LinkedInIntegrationState = REAL_STATE;
const listeners = new Set<() => void>();

function set(next: Partial<LinkedInIntegrationState>): void {
  current = { ...current, ...next };
  for (const listener of listeners) listener();
}

/** `useSyncExternalStore` needs both halves; the snapshot identity only changes on a real change. */
export function subscribeLinkedIn(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export const linkedInState = (): LinkedInIntegrationState => current;

/** The prototype scenario: approved + awaiting member authorization. Claims no real access. */
export function enableLinkedInSimulation(): void {
  set({
    simulated: true,
    apiAccessStatus: 'approved',
    memberConnectionStatus: 'not_connected',
    integrationMode: 'profile_edit_api',
  });
}

export function disableLinkedInSimulation(): void {
  set(REAL_STATE);
}

export function disconnectLinkedInMember(): void {
  set({ memberConnectionStatus: 'not_connected' });
}

export function expireLinkedInToken(): void {
  set({ memberConnectionStatus: 'token_expired' });
}

export function revokeLinkedInPermission(): void {
  set({ memberConnectionStatus: 'permission_revoked' });
}

/** The reference's pill mapping; `permission_revoked` reads as revoked, not as merely pending. */
export function linkedInPillClass(v: string): string {
  if (v === 'approved' || v === 'connected') return 'pill-active';
  if (v === 'rejected' || v === 'unavailable' || v === 'permission_revoked') return 'pill-revoked';
  return 'pill-pending';
}
